/** Detalhe do pedido selecionado: campos, itens, comissões antigas e o histórico de alterações. */
import { createResource, For, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import type { PedidoDetalhe, StatusPedido } from '@shared/types'
import { api, versaoDados } from '../../nucleo/cliente'
import { corDoStatus, moeda, numero } from './formato'
import { COLUNAS } from './Grade'

/** Registros do histórico que são só eventos (sem "de → para"; o Excluído guarda a cópia em JSON). */
const SEM_DE_PARA = new Set(['Criado', 'Restaurado', 'Excluído', 'Excluído (restaurado)'])

export function DetalhePedido(props: {
  id: number
  status: StatusPedido[] | undefined
  podeEditar: boolean
  tick: number
  aoEditar: (d: PedidoDetalhe) => void
  /** Pede a exclusão (a confirmação e o "desfazer" ficam na tela de Pedidos). */
  aoExcluir: (d: PedidoDetalhe) => void
  aoFechar: () => void
}) {
  const [d] = createResource(
    () => ({ id: props.id, v: versaoDados(), t: props.tick }),
    ({ id }) => api.pedidos.get(id),
  )
  const valorCampo = (p: PedidoDetalhe, campo: (typeof COLUNAS)[number]['campo']) => {
    if (campo === 'valor') return p.valor !== null ? moeda(p.valor) : p.valorTexto
    if (campo === 'aReceber') return p.aReceber !== null ? moeda(p.aReceber) : p.aReceberTexto
    return p[campo] as string | null
  }

  return (
    <div class="h-full flex flex-col bg-panel">
      <Show when={d.latest} fallback={<p class="p-5 text-sm text-ink-subtle">{d.loading ? 'Carregando…' : 'Pedido não encontrado.'}</p>}>
        {(p) => (
          <>
            <div class="px-5 pt-4 pb-3 border-b border-edge flex items-start gap-3">
              <span
                class="mt-1 size-3.5 shrink-0 rounded-full border border-black/25"
                style={{ background: corDoStatus(props.status, p().statusId) ?? 'transparent' }}
              />
              <div class="min-w-0 flex-1">
                <div class="font-semibold text-ink truncate">{p().cliente ?? '(sem cliente)'}</div>
                <div class="text-xs text-ink-subtle">
                  {props.status?.find((s) => s.id === p().statusId)?.nome ?? 'Sem status'} · {p().ano}
                  <Show when={p().origemAba}> · planilha, aba {p().origemAba}</Show>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={props.aoFechar} aria-label="Fechar detalhe">
                Fechar
              </Button>
            </div>

            <div class="flex-1 min-h-0 overflow-auto px-5 py-4 flex flex-col gap-5 text-sm">
              <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                <For each={COLUNAS.filter((c) => c.campo !== 'cliente' && c.campo !== 'itens')}>
                  {(c) => (
                    <>
                      <dt class="text-ink-subtle">{c.titulo}</dt>
                      <dd class="text-ink break-words" classList={{ 'tabular-nums': c.numero }}>
                        {valorCampo(p(), c.campo) || <span class="text-ink-placeholder">—</span>}
                      </dd>
                    </>
                  )}
                </For>
              </dl>

              <section class="flex flex-col gap-2">
                <h3 class="text-xs font-medium uppercase tracking-wide text-ink-subtle">Itens</h3>
                <Show when={p().itensLista.length > 0} fallback={<p class="text-ink-placeholder">Sem itens.</p>}>
                  <table class="w-full text-[13px]">
                    <tbody>
                      <For each={p().itensLista}>
                        {(i) => (
                          <tr class="border-b border-edge-muted last:border-0">
                            <td class="py-1 pr-3 w-14 text-right tabular-nums text-ink">{i.quantidade !== null ? numero(i.quantidade).replace(/,00$/, '') : '—'}</td>
                            <td class="py-1 pr-3 text-ink">{i.produto ?? i.descricao}</td>
                            <td class="py-1 text-ink-subtle">{i.complemento ?? ''}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                  <p class="text-xs text-ink-placeholder">Coluna N: {p().itens}</p>
                </Show>
              </section>

              <Show when={p().comissoes.length > 0}>
                <section class="flex flex-col gap-2">
                  <h3 class="text-xs font-medium uppercase tracking-wide text-ink-subtle">Comissões (histórico da planilha)</h3>
                  <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                    <For each={p().comissoes}>
                      {(c) => (
                        <>
                          <dt class="text-ink-subtle">{c.vendedor}</dt>
                          <dd class="tabular-nums text-ink">{c.valor !== null ? moeda(c.valor) : c.valorTexto}</dd>
                        </>
                      )}
                    </For>
                  </dl>
                </section>
              </Show>

              <section class="flex flex-col gap-2">
                <h3 class="text-xs font-medium uppercase tracking-wide text-ink-subtle">Histórico</h3>
                <Show when={p().historico.length > 0} fallback={<p class="text-ink-placeholder">Sem alterações registradas (veio assim da planilha).</p>}>
                  <ol class="flex flex-col gap-2">
                    <For each={p().historico}>
                      {(h) => (
                        <li class="text-[13px]">
                          <div class="text-xs text-ink-subtle">
                            {h.quando.slice(0, 16).split(' ').map((x, n) => (n ? x : x.split('-').reverse().join('/'))).join(' ')}
                            {h.usuario ? ` · ${h.usuario}` : ''}
                          </div>
                          <div class="text-ink">
                            <span class="font-medium">{h.campo}</span>
                            <Show when={!SEM_DE_PARA.has(h.campo)}>
                              : <span class="text-ink-subtle line-through">{h.de ?? '(vazio)'}</span> → {h.para ?? '(vazio)'}
                            </Show>
                          </div>
                        </li>
                      )}
                    </For>
                  </ol>
                </Show>
              </section>
            </div>

            <Show when={props.podeEditar}>
              <div class="px-5 py-3 border-t border-edge flex gap-2">
                <Button variant="accent" size="sm" onClick={() => props.aoEditar(p())}>Editar</Button>
                <Button variant="danger-ghost" size="sm" class="ml-auto" onClick={() => props.aoExcluir(p())}>Excluir</Button>
              </div>
            </Show>
          </>
        )}
      </Show>
    </div>
  )
}
